import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getMostExpensiveDay } from '../../utils/expensiveDayAnalytics';
import { useSubscription, setMockSubscription } from '../../hooks/useSubscription';
import PaywallModal from '../../components/PaywallModal';
import { useAuth } from '@clerk/clerk-react';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import ShareButton from '../../components/ShareButton';

const ExpensiveDayPage = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const dayStats = useMemo(() => getMostExpensiveDay(transactions), [transactions]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Get subscription status
  const { data: subscription, refetch: refetchSubscription } = useSubscription();

  // Effect to show paywall
  useEffect(() => {
    if (!subscription?.isActive) {
      setShowPaywall(true);
    }
  }, [subscription]);

  const handleClose = () => {
    if (!subscription?.isActive) {
      navigate(-1);
    } else {
      setShowPaywall(false);
    }
  };

  // Only render content when subscription is active
  if (!subscription?.isActive) {
    return (
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    );
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative flex flex-col">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      
      <div className="flex-1 px-4 sm:px-16 pt-20 pb-8 max-w-4xl mx-auto w-full flex flex-col">
        <ProgressDashes currentStep={7} totalSteps={11} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-12 mb-8"
        >
          <h1 className="text-3xl sm:text-4xl md:text-[34px] font-black text-[#221A51] leading-[140%]">
            Your Most
            <br />
            Expensive Day
          </h1>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex-1 flex flex-col"
        >
          <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51] flex-1 flex flex-col">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex-1 flex flex-col justify-center items-center gap-6 sm:gap-8"
            >
              {dayStats ? (
                <>
                  <div className="text-4xl sm:text-5xl md:text-6xl font-black text-[#9B86EA]">
                    {formatDate(new Date(dayStats.date))}
                  </div>
                  
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#221A51]">
                    {formatCurrency(dayStats.amount)}
                  </div>

                  <div className="text-lg sm:text-xl md:text-2xl text-[#221A51]/80">
                    {dayStats.isWeekend ? (
                      <>
                        At least it was a <span className="font-bold">{dayStats.dayName}</span> 😅
                      </>
                    ) : (
                      <>Oh god, and it was a <span className="font-bold">{dayStats.dayName}</span>😅</>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-xl text-[#221A51]/80">
                  No transaction data available yet
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          {dayStats && (
            <ShareButton
              title="My Most Expensive Day 💸"
              text={`On ${formatDate(new Date(dayStats.date))}, I spent ${formatCurrency(dayStats.amount)}... and it was a ${dayStats.dayName}! 😅`}
            />
          )}
        </motion.div>

        {/* Decorative stars */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="fixed bottom-0 right-0 w-48 h-48 opacity-20 -z-10"
        >
          <img src="/stars.svg" alt="" className="w-full h-full" />
        </motion.div>
      </div>
    </div>
  );
};

export default ExpensiveDayPage;
